import React from 'react';
import { Card, Button, Badge } from 'react-bootstrap';
import { Edit2, DollarSign, Calendar, Clock, Briefcase } from 'lucide-react';

const ListingCard = ({ listing, onEdit, onTogglePublish }) => {
  if (!listing) return null;
  const { title, contractType, compensation, assignment, schedule, clinical, tags, reviewStatus } = listing;
  const isPublished = reviewStatus === 'reviewed';
  return (
    <Card className="glass-card mb-3 p-3 premium-shadow animate-fade-in listing-card-premium" tabIndex={0}>
      <div className="d-flex align-items-center mb-2">
        <Briefcase size={18} className="me-2 text-info" />
        <span className="fw-semibold gradient-text fs-5">{title}</span>
        <Badge bg={contractType === 'W2' ? 'primary' : 'secondary'} className="ms-2 premium-glow-badge text-uppercase" style={{letterSpacing: 1}}>{contractType}</Badge>
        <Badge
          bg={isPublished ? 'success' : 'secondary'}
          className={`ms-2 publish-badge premium-glow-badge ${isPublished ? 'animate-shimmer' : 'animate-fade'}`}
          style={{
            letterSpacing: 1,
            fontWeight: 600,
            boxShadow: isPublished ? '0 0 12px 2px #38d99677' : '0 0 8px 0px #aaa8',
            background: isPublished
              ? 'linear-gradient(90deg, #38d996 30%, #b6ffe7 100%)'
              : 'linear-gradient(90deg, #b0b0b0 30%, #e0e0e0 100%)',
            color: isPublished ? '#fff' : '#333',
            marginRight: 8,
            marginLeft: 8,
            transition: 'all 0.4s cubic-bezier(.4,2,.6,1.2)'
          }}
        >
          {isPublished ? 'Published' : 'Draft'}
        </Badge>
        <div className="ms-auto d-flex align-items-center" style={{gap: '0.5rem'}}>
          <Button
            variant={isPublished ? 'outline-success' : 'outline-primary'}
            className={`publish-toggle-btn premium-shadow ${isPublished ? 'animate-glow' : ''}`}
            size="sm"
            style={{
              borderRadius: 6,
              padding: '0.35rem 1.1rem',
              fontWeight: 600,
              fontSize: '1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.45rem',
              cursor: 'pointer',
              borderWidth: 2,
            }}
            aria-label={isPublished ? 'Unpublish Listing' : 'Publish Listing'}
            title={isPublished ? 'Unpublish this listing (hide from candidates)' : 'Publish this listing (make visible to candidates)'}
            onClick={e => {
              e.stopPropagation();
              if (onTogglePublish) onTogglePublish();
            }}
          >
            {isPublished ? (
              <>
                <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1" viewBox="0 0 24 24"><path d="M17 13V7a5 5 0 0 0-10 0v6"/><rect width="20" height="8" x="2" y="13" rx="2"/><line x1="8" x2="8" y1="17" y2="21"/><line x1="16" x2="16" y1="17" y2="21"/></svg>
                Unpublish
              </>
            ) : (
              <>
                <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1" viewBox="0 0 24 24"><path d="M4 17V7a5 5 0 0 1 10 0v10"/><rect width="20" height="8" x="2" y="13" rx="2"/><line x1="8" x2="8" y1="21" y2="17"/><line x1="16" x2="16" y1="21" y2="17"/></svg>
                Publish
              </>
            )}
          </Button>
          <Button variant="outline-primary" size="sm" onClick={onEdit} tabIndex={-1}>
            <Edit2 size={15} /> Edit
          </Button>
        </div>
      </div>
      <div className="d-flex flex-wrap align-items-center mb-2">
        <span className="small text-muted me-3">
          <DollarSign size={13} className="me-1" />
          {compensation?.amount ? <b>${compensation.amount}</b> : 'N/A'} / {compensation?.period || 'hourly'}
        </span>
        <span className="small text-muted me-3">
          <span className="fw-semibold">Type:</span> {compensation?.contractType || contractType || 'N/A'}
        </span>
        {compensation?.includesHousing && <Badge bg="info" className="me-2 animated-glow">Housing</Badge>}
        {compensation?.includesTravel && <Badge bg="info" className="me-2 animated-glow">Travel</Badge>}
        {compensation?.malpracticeCovered && <Badge bg="info" className="me-2 animated-glow">Malpractice</Badge>}
        {compensation?.signOnBonusOffered && (
          <Badge bg="success" className="me-2 animated-glow">
            Sign-On Bonus: ${compensation.signOnBonusAmount} ({compensation.signOnBonusCommitmentYears} yrs)
          </Badge>
        )}
      </div>
      <div className="d-flex flex-wrap align-items-center mb-2">
        {assignment?.startDate && (
          <span className="small text-muted me-3"><Calendar size={13} className="me-1" />Start: {assignment.startDate}</span>
        )}
        {assignment?.types && assignment.types.length > 0 && (
          <span className="small text-muted me-3">
            <span className="fw-semibold">Assignment:</span> {assignment.types.map((t, i) => (
              <Badge key={i} bg="secondary" className="ms-1">{t}</Badge>
            ))}
          </span>
        )}
        {schedule?.pattern && (
          <span className="small text-muted me-3"><Clock size={13} className="me-1" />Schedule: {schedule.pattern}, {schedule.estimatedWeeklyHours || 0} hrs/week</span>
        )}
      </div>
      {clinical?.requiredCaseTypes && clinical.requiredCaseTypes.length > 0 && (
        <div className="small text-muted mb-1">
          <span className="fw-semibold">Clinical:</span> {clinical.requiredCaseTypes.map((type, idx) => (
            <Badge key={idx} bg="success" className="me-1">{type}</Badge>
          ))}
        </div>
      )}
      {tags && tags.length > 0 && (
        <div className="small text-muted mb-1">
          <span className="fw-semibold">Tags:</span> {tags.map((tag, idx) => (
            <Badge key={idx} bg="secondary" className="me-1">{tag}</Badge>
          ))}
        </div>
      )}
    </Card>
  );
};

export default ListingCard;
